import React, {useState} from 'react';
import {ITableGroups, TableList} from "../../../../../components/table";
import {OperacionCLGDetalleFields, OperacionCLGFields} from "../types/fields";
import CardActions from "../../../components/CardActions";
import {FormNuevaOperacionCLG} from "../types/types";
import {z} from "../../../../../lib";
import {EntityWithIdFields} from "../../../../../types";
import AforoField, {FormActualizarAforo} from "./AforoField";
import {useForm, UseFormReturn} from "react-hook-form";
import {useDetalleCLG} from '../hooks/useDetalleCLG';
import {Button, ControlledTextField} from "../../../../../components/forms";
import {ICON_MAS, ICON_SAVE} from "../../../components/SharedIcons";
import {FormInsertarEspecie, schemaFormInsertarEspecie} from "../types/especie";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {MensajePais} from "../../../../../types/enum/enum";

type SeccionCLGDatosGarantiasProps = {
  codPais: MensajePais,
  formMethods: UseFormReturn<FormNuevaOperacionCLG>,
}

const aforoSchema = z.object({
  id: z.number(),
  [OperacionCLGDetalleFields.Aforo]: z.string().min(1, 'Aforo requerido'),
  [OperacionCLGDetalleFields.OrdenSeccion]: z.number(),
  [OperacionCLGDetalleFields.Sector]: z.string(),
  [OperacionCLGDetalleFields.PermiteModificar]: z.boolean(),
  [OperacionCLGDetalleFields.CodEspecie]: z.number(),
  [OperacionCLGDetalleFields.CodSeccion]: z.number(),
  [OperacionCLGDetalleFields.Especie]: z.string(),
});

export type AforoCelda = z.infer<typeof aforoSchema>

const schemaCLGDetalleFila = z
  .object({
    [EntityWithIdFields.Id]: z.number(),
    [OperacionCLGDetalleFields.IdOperacion]: z.number(),
    [OperacionCLGDetalleFields.IdCabecera]: z.number().nullable(),
    [OperacionCLGDetalleFields.Especie]: z.string(),
    [OperacionCLGDetalleFields.OrdenEspecie]: z.number(),
    [OperacionCLGDetalleFields.OrdenCabecera]: z.number(),
    listaAforo: z.array(aforoSchema),
  })

export type CLGDetalleFila = z.infer<typeof schemaCLGDetalleFila>

function SeccionCLGDatosDetalleNuevo({codPais,formMethods}: SeccionCLGDatosGarantiasProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const {data, agregarEspecie} = useDetalleCLG(codPais)
  const [aforoRefs, setAforoRefs] = useState<Record<string, React.RefObject<any>>>({});
  const {setValue, getValues} = formMethods

  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const onUpdateAforo = (data: FormActualizarAforo) => {
    const aforosExistentes = getValues(OperacionCLGFields.ListaDetalles)

    const index = aforosExistentes.findIndex(item =>
      item[OperacionCLGDetalleFields.IdCabecera] === data[OperacionCLGDetalleFields.IdCabecera] &&
      item[OperacionCLGDetalleFields.CodSeccion] === data[OperacionCLGDetalleFields.CodSeccion] &&
      item[OperacionCLGDetalleFields.CodEspecie] === data[OperacionCLGDetalleFields.CodEspecie] &&
      item[OperacionCLGDetalleFields.Especie] === data[OperacionCLGDetalleFields.Especie]
    );

    if (index !== -1) {
      aforosExistentes[index][OperacionCLGDetalleFields.Aforo] = data[OperacionCLGDetalleFields.Aforo];
    } else {
      aforosExistentes.push({
        [OperacionCLGDetalleFields.IdOperacion]: null,
        [OperacionCLGDetalleFields.IdCabecera]: data[OperacionCLGDetalleFields.IdCabecera],
        [OperacionCLGDetalleFields.Aforo]: data[OperacionCLGDetalleFields.Aforo],
        [OperacionCLGDetalleFields.CodSeccion]: data[OperacionCLGDetalleFields.CodSeccion],
        [OperacionCLGDetalleFields.CodEspecie]: data[OperacionCLGDetalleFields.CodEspecie],
        [OperacionCLGDetalleFields.Especie]: data[OperacionCLGDetalleFields.Especie],
      });
    }
  }

  const formNuevaEspecie = useForm<FormInsertarEspecie>({
    resolver: zodResolver(schemaFormInsertarEspecie)
  })

  const onNuevaEspecie = () => {
    return new Promise<void>((resolve, reject) => {
      formNuevaEspecie.handleSubmit(
        (formData) => {
          agregarEspecie(formData.especie)
          setTimeout(() => setOpenDialog(false), 500)
          resolve()
        },
        (validationErrors) => {
          console.log(validationErrors)
          reject(validationErrors)
        }
      )()
    })
  }

  return (
    <CardActions
      title={"Detalle"}
      icon='mdi:table-edit'
      colapsable
      tableContent
      actions={
        <Stack direction={'row'} gap={2}>
          <Button variant="contained" color="primary" endIcon={ICON_MAS} onClick={() => setOpenDialog(true)}>
            Nuevo
          </Button>
          <Button variant="contained" color="primary" endIcon={ICON_SAVE}
                  onClick={() => Object.values(aforoRefs).forEach(ref => {
                    ref.current?.confirm()
                  })
                  }>
            Confirmar Aforos
          </Button>
        </Stack>

      }
    >
      {
        data.map((group, groupIndex) => {
          const columns: any[] = [];
          columns.push({label: `Especie`, value: OperacionCLGDetalleFields.Especie});
          for (let i = 0; i < group[0].listaAforo.length; i++) {

            columns.push({
              label: `Porcentaje de Aforo`, onRenderCell: (item: CLGDetalleFila) => {
                const refKey = `group${groupIndex}-item${item[OperacionCLGDetalleFields.Especie]}-aforo${i}`;
                if (!aforoRefs[refKey]) {
                  setAforoRefs(prevRefs => ({
                    ...prevRefs,
                    [refKey]: React.createRef(),
                  }));
                }

                return (
                  <AforoField
                    ref={aforoRefs[refKey]}
                    onUpdateAforo={onUpdateAforo}
                    fila={item}
                    aforo={item.listaAforo[i]}
                    permiteEditar={item.listaAforo[i][OperacionCLGDetalleFields.PermiteModificar]}
                    iniciaEditando
                  />
                );
              }
            });
          }

          const gruposColumnas: ITableGroups[] = group[0].listaAforo.map((item, index) => {
            return {
              firstColumn: index + 1 ,
              lastColumn: index + 1,
              label: item[OperacionCLGDetalleFields.Sector]
            }
          })

          if (isSmallScreen) {
            return (
              gruposColumnas.map((grupo, idx) => {
                const columnaGrupo = {
                  firstColumn: 0,
                  lastColumn: 1,
                  label: grupo.label
                }

                return (

                  <TableList
                    key={groupIndex}
                    entityList={group}
                    columns ={[columns[0], ...columns.slice(idx + 1, idx + 2)]}
                    columnGroups={[columnaGrupo]}
                    isLoading={false}
                    error={false}
                  />

                )
              })
            )
          }


          return (
            <TableList
              key={groupIndex}
              entityList={group}
              columns={columns}
              columnGroups={gruposColumnas}
              isLoading={false}
              error={false}
            />
          );

        })
      }
      <Dialog open={openDialog} fullWidth maxWidth='sm' keepMounted={false}>
        <DialogTitle>Nueva especie</DialogTitle>
        <DialogContent>
          <DialogContentText>Indique el nombre de la nueva especie.</DialogContentText>
          <Grid container spacing={4} sx={{pt: 2}}>
            <Grid item xs={12} md={6}>
              <ControlledTextField
                control={formNuevaEspecie.control}
                name='especie'
                label='Especie'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant='text'
            color='primary'
            onClick={() => setOpenDialog(false)}
          >
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            promise={onNuevaEspecie}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </CardActions>
  );
}

export default SeccionCLGDatosDetalleNuevo;
